import { x } from '@xstyled/styled-components';
import React, { useState } from 'react';
import { t } from 'ttag';
import { VisuallyHidden } from '~/lib/a11y/VisuallyHidden';
import { usePostCommentMutation } from '~/data';
import { Button } from '~/ui/buttons';
import { Spinner } from '~/ui/spinner/Spinner';
import { Sequence, useTrainingStore } from '../../state';
import { useTrainingParams } from '../../useTrainingParams';
import { getUA } from 'react-device-detect';
import { ThumbsPages, useThumbsStore } from '../Store';

interface Props {
  topic: string;
  label: string;
}

export const IssueForm = ({ topic, label }: Props) => {
  const [question, setQuestion] = useState('');
  const { learnerTrainingId, sequenceId } = useTrainingParams();
  const items = useTrainingStore(s => s.items);
  const { setPage } = useThumbsStore();
  const [postComment, { loading, data, error }] = usePostCommentMutation();

  const handleSubmit = () => {
    const sequence = items.find(
      i => i.kind === 'sequence' && i.id === sequenceId
    ) as Sequence;
    postComment({
      variables: {
        learnerTrainingUuid: learnerTrainingId!,
        question: question,
        recipient: 'easyllama',
        chapterSequenceId: sequence.chapterSequenceId,
        topic,
        userAgent: getUA
      }
    }).then(() => {
      if (!error || !data?.comment?.errors.length) {
        setPage(ThumbsPages.ThankScreen);
      }
    });
  };

  const isError = Boolean(error || data?.comment?.errors.length);
  const statusMessage = isError
    ? t`We're sorry, an unexpected error has occurred`
    : '';

  return (
    <x.div
      w={{ _: '100%', md: '280px' }}
      display="flex"
      flexDirection="column"
      alignItems="center"
      gap="md"
    >
      {/*
        CAT-1220 / WCAG 4.1.3: live region must exist on first paint (empty),
        then receive text. VisuallyHidden is position:absolute so it does not
        consume flex gap while empty.
      */}
      <VisuallyHidden role="status" aria-live="polite" aria-atomic="true">
        {statusMessage}
      </VisuallyHidden>

      {isError ? (
        <x.div
          maxWidth={{ _: '100%', md: '270px' }}
          textAlign="center"
          fontSize="18px"
        >
          {statusMessage}
        </x.div>
      ) : null}

      {loading ? (
        <x.div w={{ _: '100%', md: '270px' }}>
          <Spinner backgroundColor="transparent" />
        </x.div>
      ) : null}

      {!loading && !isError ? (
        <x.form
          w="100%"
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap="md"
        >
          <x.label
            p="3px 25px"
            fontSize="18px"
            textAlign="center"
            htmlFor="writeYourMessage"
            fontWeight="bold"
          >
            {label}
          </x.label>
          <x.textarea
            id="writeYourMessage"
            background="transparent"
            border="1px solid gray"
            placeholder="Type your message here"
            className="textareaStyleForDarTheme"
            onChange={e => setQuestion(e.target.value)}
            h="130px"
            w="100%"
            p="sm"
            resize="none"
          />
          <Button w="100%" onClick={handleSubmit}>
            {t`Submit`}
          </Button>
        </x.form>
      ) : null}
    </x.div>
  );
};
